import type {
  MatchedObjectType,
  MatchStatus,
  MatchReason,
  MatchSource,
  TransactionReconciliationStatus,
  ClientRuleType,
  ClientRuleSource,
  FlagType as DbFlagType,
  AdjustmentType,
} from '@eriko/db';

export type {
  MatchedObjectType,
  MatchStatus,
  MatchReason,
  MatchSource,
  TransactionReconciliationStatus,
  ClientRuleType,
  ClientRuleSource,
  AdjustmentType,
};

/**
 * Variance adjustment captured on a ReconciliationMatch — see schema.prisma
 * `AdjustmentType` enum. Used when the bank-line amount differs from the
 * matched bill/invoice/receipt total. QBO journal-entry writeback is
 * deferred (see PRD §11.5).
 */
export interface MatchAdjustment {
  type: AdjustmentType;
  /** Decimal as string, e.g. "12.49". Sign is informational — caller
   *  interprets based on whether bank vs entity is higher. */
  amount: string;
  description?: string;
}

/**
 * Subset of Prisma's `FlagType` enum that the reconciliation engine emits and
 * that the chat cards know how to badge / route. Stays a narrow string union so
 * the package doesn't have to re-export the full DB enum (which would collide
 * with `transaction.ts`'s domain-specific `FlagType`).
 */
export type ReconciliationFlagType =
  | 'MISSING_DOCUMENT'
  | 'AMBIGUOUS_VENDOR'
  | 'AMOUNT_MISMATCH'
  | 'MULTIPLE_POSSIBLE_MATCHES'
  | 'POSSIBLE_DUPLICATE_DOCUMENT';

export interface TransactionSummary {
  id: string;
  date: string;
  amount: string;
  description: string;
  rawDescription: string | null;
  type: 'DEBIT' | 'CREDIT';
  bankAccountName: string | null;
  merchantName: string | null;
  /** ISO 4217 code from the txn's BankAccount.currency. Caller renders the
   *  right symbol; falls back to USD if absent. */
  currency?: string | null;
}

export interface AccountingEntitySummary {
  id: string;
  externalId: string;
  entityType: string;
  displayName: string;
  amount: string | null;
  date: string | null;
  status: string | null;
}

export interface ResolvedPartyEntitySummary {
  id: string;
  displayName: string | null;
  provider: string;
}

export interface DocumentSummary {
  id: string;
  fileName: string;
  docType: string;
  vendorName: string | null;
  customerName: string | null;
  totalAmount: string | null;
  documentDate: string | null;
  referenceNumber: string | null;
  paymentStatus: string | null;
  /** Present when the document's party was resolved to an AccountingEntity. */
  resolvedPartyEntity?: ResolvedPartyEntitySummary | null;
}

export type MatchedObject =
  | { type: 'BILL' | 'INVOICE' | 'PAYOUT'; entity: AccountingEntitySummary }
  | { type: 'RECEIPT' | 'EXPENSE' | 'PAYROLL' | 'TAX_PAYMENT'; document: DocumentSummary }
  | { type: 'TRANSFER'; otherTransaction: TransactionSummary }
  | { type: 'UNKNOWN' };

export interface MatchAllocation {
  objectType: MatchedObjectType;
  objectId: string;
  amount: string;
  displayName: string;
}

export interface ReconciliationMatchDTO {
  id: string;
  organizationId: string;
  bankTransaction: TransactionSummary;
  matchedObject: MatchedObject;
  amount: string;
  confidence: number;
  matchConfidence: number;
  reason: MatchReason;
  status: MatchStatus;
  allocations?: MatchAllocation[];
  createdBy: MatchSource;
  confirmedByUserId?: string;
  rejectedReason?: string;
  notes?: string;
  confirmedAt?: string;
  /** Variance adjustment captured at match-create time, if any. */
  adjustment?: MatchAdjustment;
  createdAt: string;
  updatedAt: string;
}

export interface ReviewQueueItem {
  matchId?: string;
  transactionId: string;
  bankTransaction: TransactionSummary;
  reason: 'unmatched' | 'suggested' | 'auto-matched' | 'missing-document' | 'duplicate' | 'transfer';
  candidates?: MatchedObject[];
  topConfidence?: number;
  flag?: { type: DbFlagType; description: string };
  /** Underlying ReconciliationMatch status — lets the UI tell already-
   *  CONFIRMED rows apart from AUTO_MATCHED (both share the "Matched"
   *  group, but only AUTO_MATCHED needs reviewer confirmation). */
  matchStatus?: MatchStatus;
  /** Human-readable summary of the matched object (e.g. "Figma, Inc. Receipt #..."
   *  or "AWS Bill #INV-441"). Present on autoMatched + suggestedMatches rows.
   *  Resolved via buildMatchedObjectSummaries in apps/api at queue-build time. */
  matchedObjectSummary?: string;
  /** matchAmount from the underlying ReconciliationMatch row. Present on
   *  autoMatched + suggestedMatches rows. Decimal string. */
  matchAmount?: string;
  /** Variance captured at attach time (`adjustmentAmount` on the match, or 0
   *  when null). Drives the green confirmation strip's "Matches bank line" vs
   *  "Variance: +£X" badge after a page reload. */
  variance?: number;
}

export interface ReviewQueueGroups {
  unmatchedTransactions: ReviewQueueItem[];
  suggestedMatches: ReviewQueueItem[];
  autoMatched: ReviewQueueItem[];
  missingDocuments: ReviewQueueItem[];
  documentsWithoutPayment: ReviewQueueItem[];
  possibleDuplicates: ReviewQueueItem[];
  transfers: ReviewQueueItem[];
  // Surfaced separately so SME/admin UIs can render the right per-row
  // context badge (the engine emits these flag types, but earlier API
  // versions dropped them on the floor).
  ambiguousVendor: ReviewQueueItem[];
  amountMismatch: ReviewQueueItem[];
  // Reviewer-only: bank txn that the engine produced multiple plausible
  // candidates for. SMEs cannot disambiguate — surfaces in admin queue.
  multipleCandidates: ReviewQueueItem[];
}

export interface ReviewQueueResponse {
  groups: ReviewQueueGroups;
  counts: Record<keyof ReviewQueueGroups, number> & {
    /**
     * Unique-transaction count for SME "Needs your help" chip. Sums each
     * txn once even if it carries multiple flags (a row with
     * MISSING_DOCUMENT + AMBIGUOUS_VENDOR + AMOUNT_MISMATCH is still
     * one item of work). Equals the rendered group's length, which the
     * sum of per-category counts does NOT (it triple-counts).
     */
    needsHelpUnique?: number;
  };
  cursor?: string;
  lastRunAt?: string;
}

export interface EngineRunStats {
  totalTransactions: number;
  autoMatched: number;
  suggested: number;
  flagged: number;
  transfersDetected: number;
  autoRate: number;
}

export interface EngineRunResult {
  stats: EngineRunStats;
  matchIds: string[];
  flagIds: string[];
}

export interface ClientRuleDTO {
  id: string;
  organizationId: string;
  ruleType: ClientRuleType;
  merchantId: string | null;
  patternText: string | null;
  categoryId: string | null;
  accountCode: string | null;
  reconciliationBehavior: unknown;
  notes: string | null;
  createdBy: ClientRuleSource;
  usageCount: number;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface CreateRulePreview {
  willApplyToCount: number;
  sampleTransactions: TransactionSummary[];
}

// ── Chat-embedded reconciliation artifacts ────────────────────────────────────
//
// Two artifact shapes the AI engine emits via tool `_artifact` so chat UIs
// can render reconciliation context inline (instead of a wall of text).
// Frontend dispatch is in apps/admin and apps/dashboard ArtifactContent /
// AssistantMessage — distinguished by data.type === 'reconciliation-list' or
// 'match-confirmed'.

export interface ChatReconRow {
  /** Present for SUGGESTED / AUTO_MATCHED / CONFIRMED rows (engine-produced match). */
  matchId?: string;
  transactionId: string;
  /** ISO date. */
  date: string;
  description: string;
  /** Decimal as string, e.g. "129.99". */
  amount: string;
  type: 'DEBIT' | 'CREDIT';
  /** Human-readable "AWS Bill #INV-441" etc., when matched. */
  matchedObjectSummary?: string;
  confidence?: number;
  /**
   * Friendly per-row reason — surfaced by the card as a badge. Examples:
   *   "Eriko isn't sure who this vendor is"
   *   "Receipt needed"
   *   "Amount doesn't quite match the bill"
   */
  reasonBadge?: string;
  /**
   * Raw flag type driving the row's badge — present when the row was
   * surfaced via TransactionFlag (MISSING_DOCUMENT / AMBIGUOUS_VENDOR /
   * AMOUNT_MISMATCH / MULTIPLE_POSSIBLE_MATCHES). Lets the chat UI route
   * the row to the right action (e.g. SME Attach-receipt dialog uses
   * this as its `reason` instead of hardcoding 'missing-document').
   */
  flagReason?: ReconciliationFlagType;
  /**
   * ISO 4217 currency code (e.g. "USD", "GBP", "EUR"). Resolved server-side
   * from `OrganizationSettings.defaultCurrency` so the chat UI doesn't have to
   * guess. Absent → caller falls back to the artifact-level `currency` or USD.
   */
  currency?: string;
}

export interface ReconciliationListArtifactData {
  type: 'reconciliation-list';
  summary: {
    total: number;
    autoMatched: { count: number };
    suggested: { count: number };
    /** SME-friendly bucket: MISSING_DOCUMENT + AMBIGUOUS_VENDOR + AMOUNT_MISMATCH + plain unmatched. */
    needsHelp: { count: number };
    transfers: { count: number };
    /** Reviewer-side pile: suggested + multipleCandidates. */
    pendingReview: { count: number };
    /** Reviewer-only: bank txns with MULTIPLE_POSSIBLE_MATCHES flag. */
    multipleCandidates: { count: number };
  };
  groups: {
    /** Shown to reviewers with Approve / Reject buttons. */
    suggested: ChatReconRow[];
    /** Shown to SMEs with Attach-receipt CTA. */
    needsHelp: ChatReconRow[];
    /** Read-only chip in card header. */
    autoMatched: ChatReconRow[];
    /** Read-only chip. */
    transfers: ChatReconRow[];
    /** Reviewer-only: rows engine flagged with multiple plausible candidates. */
    multipleCandidates: ChatReconRow[];
  };
  monthlyCloseId?: string;
  /** e.g. "January 2026". */
  period?: string;
  /** ISO 4217 currency code for all rows in this artifact. Resolved from
   *  `OrganizationSettings.defaultCurrency`. Falls back to USD if unset. */
  currency?: string;
}

export interface MatchConfirmedArtifactData {
  type: 'match-confirmed';
  matchId: string;
  bankTransaction: {
    date: string;
    description: string;
    amount: string;
    type: 'DEBIT' | 'CREDIT';
    /** ISO 4217 currency code. */
    currency?: string;
  };
  /** "AWS Bill #INV-441" or similar. */
  matchedObjectSummary: string;
  status: 'CONFIRMED' | 'REJECTED';
  writeback?: {
    kind: 'success' | 'skipped';
    provider?: 'QBO' | 'XERO';
    externalId?: string;
    reason?: string;
  };
  confirmedAt: string;
}

/**
 * Runtime type guard for the `reconciliation-list` artifact payload.
 * SSE delivers artifact `data` as `Record<string, unknown>`; the guard
 * lets card consumers narrow to the typed shape without `as never` casts.
 *
 * Conservative: only checks the discriminator + the top-level shape the
 * card actually reads. Sub-fields are typed inside the artifact's own
 * interface — if the engine ever changes one of those, this returns true
 * and the card may crash at the offending field. That's the correct
 * blast radius (no silent type lie at the boundary).
 */
export function isReconciliationListArtifactData(
  data: unknown,
): data is ReconciliationListArtifactData {
  if (!data || typeof data !== 'object') return false;
  const d = data as { type?: unknown; summary?: unknown; groups?: unknown };
  return (
    d.type === 'reconciliation-list' &&
    typeof d.summary === 'object' &&
    d.summary !== null &&
    typeof d.groups === 'object' &&
    d.groups !== null
  );
}

/**
 * Discriminator for artifact types that render INLINE inside the assistant
 * chat bubble (instead of the side panel). Single source of truth — every
 * place that needs to ask "should this auto-open the side panel?" or
 * "should AssistantMessage route this to an inline card?" should call this.
 *
 * Add new inline-only types here, not in the call sites.
 */
export const INLINE_ARTIFACT_TYPES = [
  'reconciliation-list',
  'match-confirmed',
  'payment-confirmed',
] as const;
export type InlineArtifactType = (typeof INLINE_ARTIFACT_TYPES)[number];

export function isInlineArtifactType(type: string | undefined | null): boolean {
  if (!type) return false;
  return (INLINE_ARTIFACT_TYPES as readonly string[]).includes(type);
}

/** Runtime type guard for the `match-confirmed` artifact payload. */
export function isMatchConfirmedArtifactData(
  data: unknown,
): data is MatchConfirmedArtifactData {
  if (!data || typeof data !== 'object') return false;
  const d = data as {
    type?: unknown;
    matchId?: unknown;
    bankTransaction?: unknown;
    status?: unknown;
  };
  return (
    d.type === 'match-confirmed' &&
    typeof d.matchId === 'string' &&
    typeof d.bankTransaction === 'object' &&
    d.bankTransaction !== null &&
    (d.status === 'CONFIRMED' || d.status === 'REJECTED')
  );
}
